/**
 * LatencyHUD: M5.T13 on-screen latency stats overlay.
 *
 * Renders a small fixed-position panel in the bottom-left corner showing
 * live p50 / p95 / max / sample-count over a rolling PerformanceObserver
 * window. Only mounted when `bootstrapLatencyProbe()` returns an enabled
 * handle (i.e. the `?latency=1` URL flag or the localStorage opt-in is
 * set), so regular play sessions never see it.
 *
 * The HUD is the user-facing surface for the M5.T13 north-star gate:
 * "real-iPad p95 ≤ 16ms". Test protocol is documented in
 * `docs/m5-latency-profile.md`.
 *
 * Visual: paper-themed translucent card with a colored p95 number
 * (green ≤ 16, amber ≤ 24, red otherwise) so the tester can tell at a
 * glance whether the gate is met without doing the math.
 */

import { useEffect, useState } from "preact/hooks";
import type { ProbeHandle } from "./probeBootstrap.js";
import type { LatencyStats } from "./latencyProbe.js";

const ZERO_STATS: LatencyStats = { sampleCount: 0, median: 0, p95: 0, max: 0 };

type LatencyHUDProps = {
  probe: ProbeHandle;
};

function p95Class(p95Ms: number): "good" | "warn" | "bad" {
  if (p95Ms <= 16) return "good";
  if (p95Ms <= 24) return "warn";
  return "bad";
}

export function LatencyHUD({ probe }: LatencyHUDProps) {
  const [stats, setStats] = useState<LatencyStats>(probe.getStats());
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => probe.subscribe(setStats), [probe]);

  if (dismissed) return null;

  const klass = p95Class(stats.p95);

  return (
    <div
      class="latency-hud"
      data-testid="latency-hud"
      data-p95-class={klass}
      role="status"
      aria-live="polite"
    >
      <div class="latency-hud-row latency-hud-row--title">
        <span class="latency-hud-title">latency</span>
        <span class={`latency-hud-p95 latency-hud-p95--${klass}`} data-testid="latency-hud-p95">
          p95 {stats.p95.toFixed(1)}ms
        </span>
      </div>
      <div class="latency-hud-row latency-hud-row--stats">
        <span data-testid="latency-hud-p50">p50 {stats.median.toFixed(1)}</span>
        <span data-testid="latency-hud-max">max {stats.max.toFixed(1)}</span>
        <span data-testid="latency-hud-count">n={stats.sampleCount}</span>
      </div>
      <div class="latency-hud-row latency-hud-row--actions">
        <button
          type="button"
          class="latency-hud-btn"
          data-testid="latency-hud-reset"
          onClick={() => {
            probe.reset();
            setStats(ZERO_STATS);
          }}
        >
          reset
        </button>
        <button
          type="button"
          class="latency-hud-btn"
          data-testid="latency-hud-dismiss"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss latency HUD"
        >
          ×
        </button>
      </div>
    </div>
  );
}
